// @ts-nocheck
// Confirm a tracked team as playing — turns a lead into a real registration
// so the scheduler can use it. Prefilled from the tracked row, matched against
// existing teams with the same parser the registration flow uses.
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2, Check } from "lucide-react";
import { normalizeForMatch, parseTeamName } from "@/lib/teamNameParser";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
  tracked: any | null;
  onConfirmed?: () => void;
}

export function ConfirmTrackedTeamDialog({
  open,
  onOpenChange,
  eventId,
  tracked,
  onConfirmed,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [divisionLabel, setDivisionLabel] = useState("");
  const [coachName, setCoachName] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [payment, setPayment] = useState<"comped" | "owes">("owes");
  const [amount, setAmount] = useState("0");
  const [compReason, setCompReason] = useState("");

  const parsed = useMemo(() => {
    if (!query.trim()) return null;
    return parseTeamName(query, { eventYear: new Date().getFullYear() });
  }, [query]);

  useEffect(() => {
    if (!open || !tracked) return;
    setTeamId(tracked.master_team_id ?? null);
    setQuery(String(tracked.team_name ?? ""));
    setDivisionLabel(String(tracked.division_label ?? ""));
    setCoachName(String(tracked.contact_name ?? ""));
    setCoachEmail(String(tracked.contact_email ?? ""));
    setAmount(String(Number(tracked.expected_amount ?? 0) || 0));
    setPayment(Number(tracked.expected_amount ?? 0) > 0 ? "owes" : "comped");
    setCompReason("");
  }, [open, tracked?.id]);

  // Event divisions — event_divisions first, event_division_settings fallback.
  useEffect(() => {
    if (!open || !eventId) return;
    (async () => {
      const { data: ed } = await (supabase as any)
        .from("event_divisions")
        .select("id,name,age_group,gender,price_override,sort_order")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (ed?.length) {
        setDivisions(
          ed.map((d: any) => ({
            label: d.name,
            gender: d.gender === "girls" ? "girls" : "boys",
            age_group: d.age_group ?? d.name,
            price: Number(d.price_override) || 0,
          }))
        );
        return;
      }
      const { data: eds } = await (supabase as any)
        .from("event_division_settings")
        .select(
          "catalog_division_id, has_boys, has_girls, boys_price, girls_price, display_order, division_catalog:catalog_division_id(label)"
        )
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      const out: any[] = [];
      for (const s of eds ?? []) {
        const label = s.division_catalog?.label;
        if (!label) continue;
        if (s.has_boys)
          out.push({
            label: `Boys ${label}`,
            gender: "boys",
            age_group: label,
            price: Number(s.boys_price) || 0,
          });
        if (s.has_girls)
          out.push({
            label: `Girls ${label}`,
            gender: "girls",
            age_group: label,
            price: Number(s.girls_price) || 0,
          });
      }
      setDivisions(out);
    })();
  }, [open, eventId]);

  // Match-first team search on the typed name.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 3) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      const norm = normalizeForMatch(q);
      const first = norm.split(" ").slice(0, 2).join(" ");
      const { data } = await (supabase as any)
        .from("teams")
        .select("id, display_name, canonical_name, age_group, gender, clubs:club_id(name)")
        .eq("status", "active")
        .or(`display_name.ilike.%${first}%,canonical_name.ilike.%${first}%`)
        .limit(10);
      if (cancelled) return;
      setMatches(data ?? []);
      setSearching(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open]);

  const selectedDivision = divisions.find((d) => d.label === divisionLabel);

  useEffect(() => {
    if (payment === "owes" && selectedDivision && Number(amount) === 0) {
      setAmount(String(selectedDivision.price ?? 0));
    }
  }, [divisionLabel]);

  async function handleSave() {
    if (!tracked) return;
    if (!divisionLabel) {
      toast({ title: "Pick a division", variant: "destructive" });
      return;
    }
    if (payment === "comped" && !compReason.trim()) {
      toast({ title: "A reason is required to comp a team", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        tracking_id: tracked.id,
        division_label: divisionLabel,
        age_group: selectedDivision?.age_group ?? null,
        coach_name: coachName.trim() || null,
        coach_email: coachEmail.trim() || null,
        payment,
        amount: Number(amount) || 0,
        comp_reason: compReason.trim() || null,
      };
      if (teamId) {
        payload.team_id = teamId;
      } else {
        payload.new_team = {
          club_name: parsed?.club || query.trim(),
          variation: parsed?.variation ?? null,
          age_group: parsed?.ageGroup ?? selectedDivision?.age_group ?? null,
          gender: selectedDivision?.gender ?? tracked.gender ?? null,
          display_name: query.trim(),
        };
      }
      const { data, error } = await supabase.functions.invoke("confirm-tracked-team", {
        body: payload,
      });
      if (error || data?.error) {
        console.error("confirm-tracked-team failed", error, data);
        toast({
          title: "Couldn't confirm this team",
          description: data?.error || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Team confirmed as playing" });
      onConfirmed?.();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Confirm as playing</DialogTitle>
          <DialogDescription>
            Creates a registration so the scheduler can use this team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label>Team</Label>
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setTeamId(null);
              }}
              placeholder="Grind Family Chicago 17u"
            />
            {teamId ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <Check className="w-4 h-4" />
                Linked to an existing team
                <button
                  className="ml-2 text-xs underline text-slate-500"
                  onClick={() => setTeamId(null)}
                >
                  change
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 divide-y">
                {searching && (
                  <div className="p-2 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                  </div>
                )}
                {!searching && matches.length === 0 && query.trim().length >= 3 && (
                  <div className="p-2 text-xs text-slate-500">
                    No existing team matched — a new one will be created as{" "}
                    <strong>{parsed?.club || query.trim()}</strong>
                    {parsed?.variation ? ` ${parsed.variation}` : ""}
                    {parsed?.ageGroup ? ` · ${parsed.ageGroup}` : ""}
                  </div>
                )}
                {matches.map((m) => (
                  <button
                    key={m.id}
                    className="w-full text-left p-2 text-sm hover:bg-slate-50"
                    onClick={() => setTeamId(m.id)}
                  >
                    {m.display_name}
                    <span className="text-xs text-slate-500">
                      {m.clubs?.name ? ` · ${m.clubs.name}` : ""}
                      {m.age_group ? ` · ${m.age_group}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Division</Label>
            <Select value={divisionLabel} onValueChange={setDivisionLabel}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a division" />
              </SelectTrigger>
              <SelectContent>
                {divisions.map((d) => (
                  <SelectItem key={d.label} value={d.label}>
                    {d.label}
                    {d.price ? ` — $${d.price}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Coach name</Label>
              <Input value={coachName} onChange={(e) => setCoachName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Coach email</Label>
              <Input
                type="email"
                value={coachEmail}
                onChange={(e) => setCoachEmail(e.target.value)}
                placeholder="Sends an invitation"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={payment === "owes"}
                  onChange={() => setPayment("owes")}
                />
                Owes
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={payment === "comped"}
                  onChange={() => setPayment("comped")}
                />
                Comped
              </label>
              <Input
                className="h-8 w-28"
                type="number"
                min="0"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            {payment === "comped" && (
              <Textarea
                rows={2}
                value={compReason}
                onChange={(e) => setCompReason(e.target.value)}
                placeholder="Reason for the comp (required)"
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirm as playing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
